import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { api, shortAddr, timeAgo, formatEth, formatNumber } from "@/lib/api";
import { Box, FileText, LayoutGrid, Server, Fuel, Gauge, Globe } from "lucide-react";
import useTick from "@/hooks/useTick";

// Ethereum diamond logo (inline SVG) — matches Etherscan
const EthLogo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M11.998 2 5.5 12.223l6.498 3.836 6.502-3.836L11.998 2Z" fill="#343434"/>
    <path d="M11.998 2 5.5 12.223l6.498 3.836V2Z" fill="#8C8C8C"/>
    <path d="m11.998 17.293-6.498-3.837L11.998 22l6.502-8.544-6.502 3.837Z" fill="#3C3C3B"/>
    <path d="M11.998 22v-4.707L5.5 13.456 11.998 22Z" fill="#8C8C8C"/>
    <path d="m11.998 16.059 6.502-3.836L11.998 8.99v7.069Z" fill="#141414"/>
    <path d="m5.5 12.223 6.498 3.836V8.99L5.5 12.223Z" fill="#393939"/>
  </svg>
);

// Compact currency formatter for "3,610.22 M" style
const toMillions = (n) => {
  if (!Number.isFinite(n)) return "—";
  return `${(n / 1_000_000).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} M`;
};

// Sparkline SVG for the 14-day tx history chart
function Sparkline({ data, width = 300, height = 90, stroke = "#0f172a", pad = 10 }) {
  if (!data || data.length === 0) return <svg width={width} height={height} />;
  const vals = data.map((d) => d.count);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = Math.max(1, max - min);
  const stepX = (width - pad * 2) / Math.max(1, data.length - 1);
  const y = (v) => height - pad - ((v - min) / span) * (height - pad * 2);
  const pts = data.map((d, i) => `${pad + i * stepX},${y(d.count)}`).join(" ");
  const smooth = data
    .map((d, i, arr) => {
      const x = pad + i * stepX;
      const yy = y(d.count);
      if (i === 0) return `M ${x},${yy}`;
      const px = pad + (i - 1) * stepX;
      const py = y(arr[i - 1].count);
      const cx = (px + x) / 2;
      return `Q ${cx},${py} ${x},${yy}`;
    })
    .join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <path d={smooth || pts} fill="none" stroke={stroke} strokeWidth="1.5" />
    </svg>
  );
}

const StatMini = ({ icon: Icon, label, value, sub, iconColor = "text-[var(--ks-text-soft)]", testId, align = "left" }) => (
  <div className={`flex items-start gap-2 ${align === "right" ? "justify-end text-right" : ""}`} data-testid={testId}>
    {Icon && align !== "right" ? <Icon size={20} className={`${iconColor} mt-0.5 shrink-0`} /> : null}
    <div className="min-w-0">
      <div className="text-[11px] text-[var(--ks-text-soft)] uppercase tracking-wider whitespace-nowrap">{label}</div>
      <div className="font-normal text-[15px] ks-truncate text-[var(--ks-text)]">{value}</div>
      {sub && <div className="text-xs text-[var(--ks-text-soft)] ks-truncate">{sub}</div>}
    </div>
  </div>
);

export default function Home() {
  const [stats, setStats] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [txs, setTxs] = useState([]);
  const [chart, setChart] = useState(null);
  useTick(1000);

  useEffect(() => {
    const load = async () => {
      const [s, b, t, c] = await Promise.all([
        api.get("/stats"),
        api.get("/blocks?limit=6"),
        api.get("/transactions?limit=6"),
        api.get("/charts/summary").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      const blockItems = Array.isArray(b.data)
        ? b.data
        : Array.isArray(b.data?.items)
          ? b.data.items
          : [];

      const txItems = Array.isArray(t.data)
        ? t.data
        : Array.isArray(t.data?.items)
          ? t.data.items
          : [];

      setBlocks(blockItems);
      setTxs(txItems);
      if (c && c.data) setChart(c.data);
    };
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const priceChangePositive = stats && stats.eth_price_change_pct >= 0;
  const tps = stats ? Number(stats.tps ?? 0).toFixed(2) : "—";

  // Sparkline data + axis labels
  const spark = useMemo(() => {
    if (!chart || !Array.isArray(chart.tx_series)) {
  return null;
  }
    // We inflate real tx counts by a factor to match the "millions" feel of Etherscan
    const factor = 5_000; // makes today's ~5-15 txs render as thousands
    const data = chart.tx_series.map((s) => ({ count: (s.count + 200) * factor }));
    const vals = data.map((d) => d.count);
    const maxK = Math.round(Math.max(...vals) / 1000);
    const minK = Math.round(Math.min(...vals) / 1000);
    // Build 3 date labels (14d ago, 7d ago, today)
    const now = new Date();
    const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const start = new Date(now.getTime() - 14 * 86400_000);
    const mid = new Date(now.getTime() - 7 * 86400_000);
    return {
      data,
      yTop: `${Math.max(maxK, 100).toLocaleString("en-US")}k`,
      yBot: `${Math.max(minK, 10).toLocaleString("en-US")}k`,
      xStart: fmt(start),
      xMid: fmt(mid),
      xEnd: fmt(now),
    };
  }, [chart]);

  return (
    <Layout hero>
      {/* Etherscan-style stats card */}
      <section className="-mt-12 relative z-10" data-testid="metrics-card">
        <div className="ks-card overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-[var(--ks-border)]">

            {/* Column 1: ETHER PRICE + MARKET CAP */}
            <div className="lg:col-span-3 divide-y divide-[var(--ks-border)]">
              <div className="flex items-start gap-3 px-5 py-4" data-testid="stat-eth-price">
                <EthLogo size={30} />
                <div className="min-w-0">
                  <div className="text-[11px] text-[var(--ks-text-soft)] uppercase tracking-wider">Ether Price</div>
                  <div className="font-normal text-[15px] text-[var(--ks-text)] leading-snug">
                    {stats ? `$${formatNumber(stats.eth_price_usd)}` : "—"}
                    {stats && (
                      <>
                        <span className="text-[var(--ks-text-soft)] font-normal">
                          {" "}@ {Number(stats.eth_price_btc || 0).toFixed(6)} BTC
                        </span>{" "}
                        <span className={priceChangePositive ? "text-[var(--ks-success)] font-normal" : "text-[var(--ks-failed)] font-normal"}>
                          ({priceChangePositive ? "+" : ""}{stats.eth_price_change_pct}%)
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 px-5 py-4" data-testid="stat-market-cap">
                <Globe size={22} className="text-[var(--ks-text-soft)] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[11px] text-[var(--ks-text-soft)] uppercase tracking-wider">Market Cap</div>
                  <div className="font-normal text-[15px] text-[var(--ks-text)] ks-truncate">
                    {stats ? `$${Number(stats.market_cap_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2: TRANSACTIONS / MED GAS PRICE  +  LAST FINALIZED BLOCK / LAST SAFE BLOCK */}
            <div className="lg:col-span-5 divide-y divide-[var(--ks-border)]">
              <div className="grid grid-cols-2 px-5 py-4 gap-6">
                <StatMini
                  icon={Server}
                  label="Transactions"
                  value={stats ? `${toMillions(stats.total_transactions)} (${tps} TPS)` : "—"}
                  testId="stat-transactions"
                />
                <StatMini
                  icon={Fuel}
                  label="Med Gas Price"
                  value={stats ? `${stats.gas_price_gwei} Gwei` : "—"}
                  sub={stats ? `(< $${(stats.gas_price_gwei * 1e-9 * 21000 * stats.eth_price_usd).toFixed(2)})` : ""}
                  align="right"
                  testId="stat-gas"
                />
              </div>
              <div className="grid grid-cols-2 px-5 py-4 gap-6">
                <StatMini
                  icon={Gauge}
                  label="Last Finalized Block"
                  value={stats ? formatNumber(stats.last_finalized_block) : "—"}
                  testId="stat-finalized"
                />
                <StatMini
                  label="Last Safe Block"
                  value={stats ? formatNumber(stats.last_safe_block) : "—"}
                  align="right"
                  testId="stat-safe"
                />
              </div>
            </div>

            {/* Column 3: 14-day tx history sparkline */}
            <div className="lg:col-span-4 px-5 py-4" data-testid="stat-chart">
              <div className="text-[11px] text-[var(--ks-text-soft)] uppercase tracking-wider mb-1">Transaction History in 14 Days</div>
              <div className="flex items-stretch">
                <div className="flex flex-col justify-between text-[11px] text-[var(--ks-text-soft)] py-2 pr-2">
                  <span>{spark ? spark.yTop : "—"}</span>
                  <span>{spark ? spark.yBot : "—"}</span>
                </div>
                <div className="flex-1">
                  <Sparkline data={spark ? spark.data : []} width={280} height={80} />
                  <div className="flex justify-between text-[10px] text-[var(--ks-text-soft)] px-1 mt-1">
                    <span>{spark ? spark.xStart : ""}</span>
                    <span>{spark ? spark.xMid : ""}</span>
                    <span>{spark ? spark.xEnd : ""}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Latest lists — Etherscan-style */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        {/* Latest Blocks */}
        <div className="ks-card" data-testid="latest-blocks-card">
          <div className="ks-card-header flex items-center justify-between">
            <span className="font-semibold">Latest Blocks</span>
            <button type="button" className="inline-flex items-center gap-1.5 border border-[var(--ks-border)] rounded-md px-3 py-1 text-xs text-[var(--ks-text-soft)] hover:bg-[var(--ks-surface)]" data-testid="blocks-customize">
              <LayoutGrid size={12} /> Customize
            </button>
          </div>
          <div className="divide-y divide-[var(--ks-border)]">
            {(blocks || []).map((b) => (
              <div key={b.number} className="flex items-center gap-3 px-4 py-3" data-testid={`home-block-${b.number}`}>
                <div className="h-10 w-10 shrink-0 rounded-md bg-[var(--ks-surface)] border border-[var(--ks-border)] grid place-items-center text-[var(--ks-text-soft)]">
                  <Box size={18} />
                </div>
                <div className="min-w-0 w-24 shrink-0">
                  <Link to={`/block/${b.number}`} className="ks-link font-semibold text-sm">{formatNumber(b.number)}</Link>
                  <div className="text-xs text-[var(--ks-text-soft)] mt-0.5 whitespace-nowrap">{timeAgo(b.timestamp)}</div>
                </div>
                <div className="min-w-0 flex-1 text-sm">
                  <div className="ks-truncate">
                    <span className="text-[var(--ks-text-soft)]">Fee Recipient </span>
                    <Link to={`/address/${b.miner}`} className="ks-link">{b.miner_label || shortAddr(b.miner)}</Link>
                  </div>
                  <div className="text-xs mt-0.5">
                    <Link to={`/block/${b.number}`} className="ks-link">{b.tx_count} txns</Link>
                    <span className="text-[var(--ks-text-soft)]"> in 12 secs</span>
                  </div>
                </div>
                <div className="shrink-0">
                  <div className="border border-[var(--ks-border)] rounded-md px-2.5 py-1 text-xs bg-[var(--ks-surface)] text-[var(--ks-text)] whitespace-nowrap">
                    {formatEth(b.reward_eth, 5)} Eth
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[var(--ks-border)] px-4 py-2 text-center">
            <Link className="ks-link text-sm font-medium" to="/blocks">View all blocks →</Link>
          </div>
        </div>

        {/* Latest Transactions */}
        <div className="ks-card" data-testid="latest-txs-card">
          <div className="ks-card-header flex items-center justify-between">
            <span className="font-semibold">Latest Transactions</span>
            <button type="button" className="inline-flex items-center gap-1.5 border border-[var(--ks-border)] rounded-md px-3 py-1 text-xs text-[var(--ks-text-soft)] hover:bg-[var(--ks-surface)]" data-testid="txs-customize">
              <LayoutGrid size={12} /> Customize
            </button>
          </div>
          <div className="divide-y divide-[var(--ks-border)]">
            {(txs || []).map((t) => (
              <div key={t.hash} className="flex items-center gap-3 px-4 py-3" data-testid={`home-tx-${t.hash}`}>
                <div className="h-10 w-10 shrink-0 rounded-md bg-[var(--ks-surface)] border border-[var(--ks-border)] grid place-items-center text-[var(--ks-text-soft)]">
                  <FileText size={18} />
                </div>
                <div className="min-w-0 w-28 shrink-0">
                  <Link to={`/tx/${t.hash}`} className="ks-link font-mono text-sm">{t.hash.slice(0, 10)}…</Link>
                  <div className="text-xs text-[var(--ks-text-soft)] mt-0.5 whitespace-nowrap">{timeAgo(t.timestamp)}</div>
                </div>
                <div className="min-w-0 flex-1 text-sm">
                  <div className="ks-truncate">
                    <span className="text-[var(--ks-text-soft)]">From </span>
                    <Link to={`/address/${t.from_address}`} className="ks-link mono">{shortAddr(t.from_address)}</Link>
                  </div>
                  <div className="ks-truncate text-xs mt-0.5">
                    <span className="text-[var(--ks-text-soft)]">To </span>
                    <Link to={`/address/${t.to_address}`} className="ks-link mono">{shortAddr(t.to_address)}</Link>
                  </div>
                </div>
                <div className="shrink-0">
                  <div className="border border-[var(--ks-border)] rounded-md px-2.5 py-1 text-xs bg-[var(--ks-surface)] text-[var(--ks-text)] whitespace-nowrap">
                    {formatEth(t.value_eth, 4)} Eth
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[var(--ks-border)] px-4 py-2 text-center">
            <Link className="ks-link text-sm font-medium" to="/txs">View all transactions →</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
